import { zodResolver } from "@hookform/resolvers/zod";
import type { JobDetailsData, JobDetailsSection } from "@stryker/core/types/compositeSections";
import type { ExpandStatus } from "@stryker/core/types/expandable";
import type { ExpandableCards } from "@stryker/core/types/jobs";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TextInput } from "@/components/ui/textInput";
import { createEditJobOptionsQuery, useConfigOptions } from "@/hooks/useConfigOptions";
import { ConfirmJobDetails } from "@/pages/admin/compositeJob/components/JobDetails/ConfirmJobDetails";

import ExpandableSection from "../../../../../components/job/ExpandableSection";

const jobDetailsSchema = z.object({
  job_number: z.string({ message: "Job number is required" }).refine((jobNumber) => /^[a-zA-Z0-9]+$/.test(jobNumber), {
    message: "Job number can only contain letters and numbers",
  }),
  plant_name: z.string({ message: "Plant Name is required" }).min(1, { message: "Plant Name is required" }),
  service_name: z.string({ message: "Service is required" }).min(1, { message: "Service is required" }),
  design_code: z.string({ message: "Design code is required" }).min(1, { message: "Design code is required" }),
  repair_system: z.string({ message: "Repair system is required" }).min(1, { message: "Repair system is required" }),
  surface_preparation: z
    .string({ message: "Surface preparation is required" })
    .min(1, { message: "Surface preparation is required" }),
  branch: z.string().min(1, "Branch is required"),
});

type JobDetailsSchema = z.infer<typeof jobDetailsSchema>;

const cardName = "jobDetails";
type CompositeJobDetailsProps = {
  savedJobDetails: JobDetailsSection;
  toggleExpandableSection: (section: ExpandableCards) => void;
  setJobDetails: (payload: { data: JobDetailsData; status: ExpandStatus }) => void;
};
export function CompositeJobDetails({
  savedJobDetails,
  toggleExpandableSection,
  setJobDetails,
}: CompositeJobDetailsProps) {
  const [confirmData, setConfirmData] = useState<JobDetailsSchema | null>(null);

  const defaultValue: JobDetailsSchema = {
    branch: savedJobDetails.data.branch,
    job_number: savedJobDetails.data.job_number ?? "",
    plant_name: savedJobDetails.data.plant_name ? String(savedJobDetails.data.plant_name) : "",
    service_name: savedJobDetails.data.service_name ? String(savedJobDetails.data.service_name) : "",
    design_code: savedJobDetails.data.design_code ?? "",
    repair_system: savedJobDetails.data.repair_system ? String(savedJobDetails.data.repair_system) : "",
    surface_preparation: savedJobDetails.data.surface_preparation
      ? String(savedJobDetails.data.surface_preparation)
      : "",
  };

  const form = useForm<JobDetailsSchema>({
    resolver: zodResolver(jobDetailsSchema),
    values: defaultValue,
  });
  const lastSavedDataRef = useRef<string | null>(null);

  useEffect(() => {
    const serialized = JSON.stringify(defaultValue);
    if (serialized !== lastSavedDataRef.current) {
      form.reset(defaultValue);
      lastSavedDataRef.current = serialized;
    }
  }, [defaultValue, form]);

  const { data: designCodesOptions = [] } = useQuery({
    ...createEditJobOptionsQuery,
    select(response) {
      return (response.design_codes ?? []).map((code) => {
        if (typeof code === "string") {
          return code;
        }

        return code.name;
      });
    },
  });

  const { repairSystemOptions, surfacePreparationOptions } = useConfigOptions();

  const handleSaveJobDetails = () => {
    if (!confirmData) return;
    setConfirmData(null);
    const payload: JobDetailsData = {
      branch: confirmData.branch,
      job_number: confirmData.job_number,
      plant_name: confirmData.plant_name,
      service_name: confirmData.service_name,
      design_code: confirmData.design_code,
      surface_preparation: Number(confirmData.surface_preparation),
      repair_system: Number(confirmData.repair_system),
    };
    setJobDetails({ data: payload, status: "done" });
  };

  return (
    <>
      <ExpandableSection
        status={savedJobDetails.status}
        setIsExpanded={() => toggleExpandableSection(cardName)}
        title="Job Details"
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(setConfirmData)} className="flex flex-col flex-wrap gap-6">
            <div className="flex flex-wrap gap-4 [&>*]:min-w-[200px] md:items-end">
              <FormField
                control={form.control}
                name="job_number"
                render={({ field }) => (
                  <FormItem className="flex flex-1 flex-col">
                    <FormLabel className="text-left">Job Number</FormLabel>
                    <FormControl>
                      <TextInput placeholder="Job number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="plant_name"
                render={({ field }) => (
                  <FormItem className="flex flex-1 flex-col">
                    <FormLabel className="text-left">Plant Name</FormLabel>
                    <FormControl>
                      <TextInput placeholder="Plant name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="service_name"
                render={({ field }) => (
                  <FormItem className="flex flex-1 flex-col">
                    <FormLabel className="text-left">Service</FormLabel>
                    <FormControl>
                      <TextInput placeholder="Service name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="design_code"
                control={form.control}
                render={({ field }) => {
                  return (
                    <FormItem className="flex flex-1 flex-col">
                      <FormLabel>Design Code</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue defaultValue={field.value} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent ref={field.ref}>
                          {designCodesOptions.map((option) => {
                            return (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <FormField
                name="repair_system"
                control={form.control}
                render={({ field }) => {
                  return (
                    <FormItem className="flex flex-1 flex-col">
                      <FormLabel>Repair system</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue defaultValue={field.value} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent ref={field.ref}>
                          {repairSystemOptions.map((option) => {
                            return (
                              <SelectItem key={option.value} value={String(option.value)}>
                                {option.title}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <FormField
                name="surface_preparation"
                control={form.control}
                render={({ field }) => {
                  return (
                    <FormItem className="flex flex-1 flex-col">
                      <FormLabel>Surface preparation</FormLabel>
                      <Select onValueChange={field.onChange} value={String(field.value)}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue defaultValue={field.value} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent ref={field.ref}>
                          {surfacePreparationOptions.map((option) => {
                            return <OptionWithHover key={option.value} option={option} />;
                          })}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <FormField
                control={form.control}
                name="branch"
                render={({ field }) => (
                  <FormItem className="flex flex-1 flex-col">
                    <FormLabel className="text-left">Branch</FormLabel>
                    <FormControl>
                      <TextInput placeholder="Branch" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <Button className="w-full self-end md:w-fit" type="submit">
              Save
            </Button>
          </form>
        </Form>
      </ExpandableSection>
      <ConfirmJobDetails
        confirmData={confirmData}
        handleSaveJobDetails={handleSaveJobDetails}
        setConfirmData={setConfirmData}
        repairSystemOptions={repairSystemOptions}
        surfacePreparationOptions={surfacePreparationOptions}
      />
    </>
  );
}

function OptionWithHover({ option }: { option: { value: string | number; title: string; long_name: string } }) {
  const [isShown, setIsShown] = useState(false);
  return (
    <div
      className="bg-red flex flex-1 cursor-pointer flex-col"
      onMouseEnter={() => setIsShown(true)}
      onMouseLeave={() => setIsShown(false)}
    >
      <SelectItem value={String(option.value)}>
        {isShown && option.long_name ? option.long_name : option.title}
      </SelectItem>
    </div>
  );
}
